
import React from 'react';
import { motion } from 'framer-motion';
import { CalendarDays, Plus, Target, CheckCircle, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { ViewMode } from '../TaskViewContainer';
import { Button } from '@/components/ui/button';

interface EmptyTaskStateProps {
  viewMode: ViewMode;
  currentDate: Date;
  onCreateTask?: () => void;
}

const EmptyTaskState: React.FC<EmptyTaskStateProps> = ({ viewMode, currentDate, onCreateTask }) => {
  const handleCreateTask = () => {
    if (onCreateTask) {
      onCreateTask();
    }
  };

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center max-w-2xl mx-auto">
      {/* Animated illustration with floating elements */}
      <div className="relative mb-8">
        {/* Background floating circles */}
        <div className="absolute inset-0 -m-8">
          <motion.div
            className="absolute top-0 left-0 w-4 h-4 bg-purple-400/30 rounded-full"
            animate={{ 
              y: [0, -10, 0],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{ 
              repeat: Infinity, 
              duration: 3,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute top-4 right-2 w-3 h-3 bg-blue-400/40 rounded-full"
            animate={{ 
              y: [0, -8, 0],
              opacity: [0.4, 0.7, 0.4]
            }}
            transition={{ 
              repeat: Infinity, 
              duration: 2.5,
              delay: 0.5,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-2 left-4 w-2 h-2 bg-green-400/50 rounded-full"
            animate={{ 
              y: [0, -6, 0],
              opacity: [0.5, 0.8, 0.5]
            }}
            transition={{ 
              repeat: Infinity, 
              duration: 2.8,
              delay: 1,
              ease: "easeInOut"
            }}
          />
        </div>
        
        {/* Main icon with gradient background */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-blue-500/20 dark:from-purple-400/10 dark:to-blue-400/10 rounded-full blur-xl"></div>
          <motion.div
            className="relative z-10 bg-gradient-to-br from-white/60 to-white/30 dark:from-white/10 dark:to-white/5 backdrop-blur-sm border border-white/20 dark:border-white/10 rounded-full p-6 shadow-lg"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            whileHover={{ scale: 1.05 }}
          >
            <motion.div
              animate={{ 
                rotate: [0, 5, -5, 0],
              }}
              transition={{ 
                repeat: Infinity, 
                duration: 4,
                ease: "easeInOut"
              }}
            >
              <CalendarDays className="h-16 w-16 text-purple-600 dark:text-purple-400" />
            </motion.div>
          </motion.div>
        </div>
      </div>
      
      {/* Compelling headline */}
      <motion.h3 
        className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600 dark:from-purple-400 dark:to-blue-400 mb-3"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        Ready to Get Things Done?
      </motion.h3>
      
      {/* Benefit-focused description */}
      <motion.p 
        className="text-gray-600 dark:text-gray-300 mb-8 max-w-md leading-relaxed"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        Turn scattered thoughts into laser-focused action plans with zero effort.
      </motion.p>

      {/* Benefits grid */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 max-w-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <div className="flex flex-col items-center text-center p-3">
          <Target className="h-8 w-8 text-purple-500 dark:text-purple-400 mb-2" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Stay Focused</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Clear priorities</p>
        </div>
        <div className="flex flex-col items-center text-center p-3">
          <Clock className="h-8 w-8 text-blue-500 dark:text-blue-400 mb-2" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Meet Deadlines</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Never miss important dates</p>
        </div>
        <div className="flex flex-col items-center text-center p-3">
          <CheckCircle className="h-8 w-8 text-green-500 dark:text-green-400 mb-2" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Track Progress</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">See your achievements</p>
        </div>
      </motion.div>
      
      {/* Call-to-action button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.5 }}
      >
        <Button 
          onClick={handleCreateTask}
          className="relative group overflow-hidden bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-4 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
        >
          {/* Shimmer effect */}
          <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12" />
          
          {/* Button content */}
          <div className="relative flex items-center justify-center gap-3">
            <Plus className="w-5 h-5" />
            <span>Create Your First Task</span>
          </div>
        </Button>
      </motion.div>

      {/* Subtle hint about current period */}
      <motion.p 
        className="text-xs text-gray-400 dark:text-gray-500 mt-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        {viewMode === 'day' 
          ? `No tasks scheduled for ${format(currentDate, 'MMMM d, yyyy')}` 
          : viewMode === 'week'
            ? 'No tasks scheduled for this week'
            : 'No tasks scheduled for this month'
        }
      </motion.p>
    </div>
  );
};

export default EmptyTaskState;
